import { ApplicationConfig, provideZoneChangeDetection, isDevMode, LOCALE_ID } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { provideRouter } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';

// Register the French locale so Angular pipes (date, currency, decimal) and
// PrimeNG components render numbers and dates with French conventions
// (dd/MM/yyyy, "1 234,56 €", "lundi", etc.) without per-call locale args.
registerLocaleData(localeFr);
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Nora from '@primeng/themes/nora';
import { authInterceptor } from './core/interceptors/auth';
import { errorInterceptor } from './core/interceptors/error';
import { cacheInterceptor } from './core/interceptors/cache';
import { loadingInterceptor } from './core/interceptors/loading';
import { offlineInterceptor } from './core/interceptors/offline';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { patchPrimeNgAutoFocus } from './core/primeng-autofocus-fix';

// Appliqué avant le bootstrap : la directive ne doit jamais poser d'attribut
// `autofocus` que l'application n'a pas demandé (voir le fichier importé).
patchPrimeNgAutoFocus();

// Palette : tokens de `styles.css`, remappés par le template de design actif
// (`data-design` sur <html>, voir core/services/design-templates.ts).
// Les valeurs doivent rester identiques aux tokens de `styles.css` : PrimeNG et
// Tailwind peignent les mêmes écrans, deux teintes différentes se verraient.
// Nora preset is designed for Tailwind CSS compatibility
const IcakenePreset = definePreset(Nora, {
  semantic: {
    primary: {
      50: 'var(--color-primary-50)',
      100: 'var(--color-primary-100)',
      200: 'var(--color-primary-200)',
      300: 'var(--color-primary-300)',
      400: 'var(--color-primary-400)',
      500: 'var(--color-primary-500)',
      600: 'var(--color-primary-600)',
      700: 'var(--color-primary-700)',
      800: 'var(--color-primary-800)',
      900: 'var(--color-primary-900)',
      950: 'var(--color-primary-950)',
    },
    colorScheme: {
      light: {
        primary: {
          color: 'var(--color-primary-base)',
          contrastColor: 'var(--color-on-primary)',
          hoverColor: 'var(--color-primary-hover)',
          activeColor: 'var(--color-primary-hover)',
        },
        highlight: {
          background: 'var(--color-primary-surface)',
          focusBackground: 'var(--color-primary-surface)',
          color: 'var(--color-primary-text)',
          focusColor: 'var(--color-primary-text)',
        },
        // Neutres graphite plutôt qu'ardoise : la seule teinte de l'écran doit
        // venir d'un rôle sémantique, pas du gris de fond.
        surface: {
          0: '#ffffff',
          50: '#f5f6f8',
          100: '#eff1f4',
          200: '#e4e6ea',
          300: '#d2d4d9',
          400: '#86868b',
          500: '#57575c',
          600: '#4a4a4f',
          700: '#3a3a3c',
          800: '#2c2e33',
          900: '#1d1d1f',
          950: '#111214',
        },
      },
      dark: {
        primary: {
          color: 'var(--color-primary-base)',
          contrastColor: 'var(--color-on-primary)',
          hoverColor: 'var(--color-primary-hover)',
          activeColor: 'var(--color-primary-hover)',
        },
        highlight: {
          background: 'var(--color-primary-surface)',
          focusBackground: 'var(--color-primary-surface)',
          color: 'var(--color-primary-text)',
          focusColor: 'var(--color-primary-text)',
        },
        surface: {
          0: '#1c1d20',
          50: '#26282c',
          100: '#2c2e33',
          200: '#303237',
          300: '#3d3f45',
          400: '#7c7c82',
          500: '#a1a1a6',
          600: '#d4d4d8',
          700: '#e4e4e7',
          800: '#f5f5f7',
          900: '#f5f5f7',
          950: '#ffffff',
        },
      },
    },
  },
  primitive: {
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '8px',
      md: '10px',
      lg: '14px',
      xl: '18px',
    },
  },
} as any);

export const appConfig: ApplicationConfig = {
  providers: [
    // App-wide locale — drives Angular DatePipe / CurrencyPipe / DecimalPipe
    // formatting and PrimeNG component defaults.
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    // L'intercepteur hors-ligne se place après l'authentification (la requête
    // rejouée doit porter son jeton) et avant celui d'erreur, pour qu'une
    // coupure réseau serve le cache local au lieu d'afficher une alerte.
    provideHttpClient(
      withInterceptors([
        loadingInterceptor,
        errorInterceptor,
        authInterceptor,
        offlineInterceptor,
        cacheInterceptor,
      ]),
    ),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: IcakenePreset,
        options: {
          // Matches the `app-dark` class ThemeService / UserPreferencesService set on <html>,
          // so PrimeNG's own dark tokens switch alongside styles/dark-mode.css.
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            // Doit reproduire à l'identique la déclaration de styles.css
            // (`@layer theme, base, primeng, components, utilities;`). PrimeNG
            // injecte sa propre déclaration dans un <style> placé en tête du
            // document : la première déclaration gagne, et l'ancien ordre
            // « tailwind-base, primeng, tailwind-utilities » installait les
            // couches Tailwind `base` et `utilities` APRÈS primeng — le Preflight
            // (`button { background: transparent }`) effaçait alors le fond de
            // tous les boutons PrimeNG.
            order: 'theme, base, primeng, components, utilities',
          },
        },
      },
      ripple: true,
      translation: {
        startsWith: 'Commence par',
        contains: 'Contient',
        notContains: 'Ne contient pas',
        endsWith: 'Termine par',
        equals: 'Égal à',
        notEquals: 'Différent de',
        noFilter: 'Aucun filtre',
        lt: 'Inférieur à',
        lte: 'Inférieur ou égal à',
        gt: 'Supérieur à',
        gte: 'Supérieur ou égal à',
        is: 'Est',
        isNot: 'N\'est pas',
        before: 'Avant',
        after: 'Après',
        dateIs: 'Date est',
        dateIsNot: 'Date n\'est pas',
        dateBefore: 'Date avant',
        dateAfter: 'Date après',
        clear: 'Effacer',
        apply: 'Appliquer',
        matchAll: 'Correspond à tout',
        matchAny: 'Correspond à au moins un',
        addRule: 'Ajouter une règle',
        removeRule: 'Supprimer la règle',
        accept: 'Oui',
        reject: 'Non',
        choose: 'Choisir',
        upload: 'Téléverser',
        cancel: 'Annuler',
        completed: 'Terminé',
        pending: 'En attente',
        fileSizeTypes: ['o', 'Ko', 'Mo', 'Go', 'To', 'Po', 'Eo', 'Zo', 'Yo'],
        dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
        monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
        chooseYear: 'Choisir l\'année',
        chooseMonth: 'Choisir le mois',
        chooseDate: 'Choisir la date',
        prevDecade: 'Décennie précédente',
        nextDecade: 'Décennie suivante',
        prevYear: 'Année précédente',
        nextYear: 'Année suivante',
        prevMonth: 'Mois précédent',
        nextMonth: 'Mois suivant',
        prevHour: 'Heure précédente',
        nextHour: 'Heure suivante',
        prevMinute: 'Minute précédente',
        nextMinute: 'Minute suivante',
        prevSecond: 'Seconde précédente',
        nextSecond: 'Seconde suivante',
        am: 'AM',
        pm: 'PM',
        dateFormat: 'dd/mm/yy',
        firstDayOfWeek: 1,
        today: 'Aujourd\'hui',
        weekHeader: 'Sem',
        weak: 'Faible',
        medium: 'Moyen',
        strong: 'Fort',
        passwordPrompt: 'Saisissez un mot de passe',
        emptyMessage: 'Aucun résultat trouvé',
        emptyFilterMessage: 'Aucun résultat trouvé',
        searchMessage: '{0} résultats disponibles',
        selectionMessage: '{0} éléments sélectionnés',
        emptySelectionMessage: 'Aucun élément sélectionné',
        emptySearchMessage: 'Aucun résultat trouvé',
        aria: {
          trueLabel: 'Vrai',
          falseLabel: 'Faux',
          nullLabel: 'Non sélectionné',
          star: '1 étoile',
          stars: '{star} étoiles',
          selectAll: 'Tout sélectionner',
          unselectAll: 'Tout désélectionner',
          close: 'Fermer',
          previous: 'Précédent',
          next: 'Suivant',
          navigation: 'Navigation',
          scrollTop: 'Haut de page',
          moveTop: 'Déplacer en haut',
          moveUp: 'Monter',
          moveDown: 'Descendre',
          moveBottom: 'Déplacer en bas',
          moveToTarget: 'Déplacer vers la cible',
          moveToSource: 'Déplacer vers la source',
          moveAllToTarget: 'Tout déplacer vers la cible',
          moveAllToSource: 'Tout déplacer vers la source',
          pageLabel: 'Page {page}',
          firstPageLabel: 'Première page',
          lastPageLabel: 'Dernière page',
          nextPageLabel: 'Page suivante',
          prevPageLabel: 'Page précédente',
          rowsPerPageLabel: 'Lignes par page',
          jumpToPageDropdownLabel: 'Aller à la page',
          jumpToPageInputLabel: 'Numéro de page',
          selectRow: 'Sélectionner la ligne',
          unselectRow: 'Désélectionner la ligne',
          expandRow: 'Développer la ligne',
          collapseRow: 'Réduire la ligne',
          showFilterMenu: 'Afficher le menu de filtres',
          hideFilterMenu: 'Masquer le menu de filtres',
          filterOperator: 'Opérateur de filtre',
          filterConstraint: 'Contrainte de filtre',
          editRow: 'Éditer la ligne',
          saveEdit: 'Enregistrer',
          cancelEdit: 'Annuler',
          listView: 'Vue en liste',
          gridView: 'Vue en grille',
          slide: 'Diapositive',
          slideNumber: '{slideNumber}',
          zoomImage: 'Zoomer l\'image',
          zoomIn: 'Zoomer',
          zoomOut: 'Dézoomer',
          rotateRight: 'Rotation à droite',
          rotateLeft: 'Rotation à gauche',
          listLabel: 'Liste d\'options',
          selectColor: 'Sélectionner une couleur',
          removeLabel: 'Retirer',
          browseFiles: 'Parcourir les fichiers',
        },
      },
    }),
    MessageService,
    // Instance racine : le <p-confirmdialog> unique de layout/main-layout.ts l'écoute.
    ConfirmationService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    ...provideTranslateService({ fallbackLang: 'fr' }),
    ...provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
  ],
};
